import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { singleEvidenceSelectors } from "store/admin-slices/single-evidence-slice/selectors";
import { singleEvidenceActions } from "store/admin-slices/single-evidence-slice/slice";
import { LOADING_STATUSES, RESIDENT_TYPES } from "utils/constants";

const onlyNumbersRegex = new RegExp("^-?\\d+$");

export const useFilters = ({ property_id }) => {
  const dispatch = useDispatch();

  const {
    residentType,
    ranges,
    floats,
    dates,
    appliedFilters,
    numbers,
    selectedOptions,
    selects,
    filters,
    datesValues,
    getSubjectPropertyStatus
  } = useSelector(singleEvidenceSelectors);

  const setSelectedOptions = (payload) => {
    if (typeof payload === "function") {
      return dispatch(singleEvidenceActions.setSelectedOptions(payload(selectedOptions)));
    } else {
      dispatch(singleEvidenceActions.setSelectedOptions(payload));
    }
  };

  const setFilters = (payload) => {
    if (typeof payload === "function") {
      return dispatch(singleEvidenceActions.setFilters(payload(filters)));
    } else {
      dispatch(singleEvidenceActions.setFilters(payload));
    }
  };

  const setNumbers = (payload) => {
    if (typeof payload === "function") {
      return dispatch(singleEvidenceActions.setNumbers(payload(numbers)));
    } else {
      dispatch(singleEvidenceActions.setNumbers(payload));
    }
  };

  const setDatesValues = (payload) => {
    if (typeof payload === "function") {
      return dispatch(singleEvidenceActions.setDatesValues(payload(datesValues)));
    } else {
      dispatch(singleEvidenceActions.setDatesValues(payload));
    }
  };

  const handleSelectChange = (name, newOptions) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: newOptions,
    }));
  };

  const handleFirstFilterChange = (e, key) => {
    const value = onlyNumbersRegex.test(e.target.value) ? e.target.value : null;

    setFilters({
      ...filters,
      [key]: {
        ...filters[key],
        start: value,
        end: filters[key]?.end || null,
      },
    });
  };

  const handleSecondFilterChange = (e, key) => {
    const value = onlyNumbersRegex.test(e.target.value) ? e.target.value : null;

    setFilters({
      ...filters,
      [key]: {
        ...filters[key],
        end: value,
        start: filters[key]?.start || null,
      }
    });
  };

  const handleDateChange = (e, key) => {
    setDatesValues((prevState) => ({
      ...prevState,
      [key]: e.target.value,
    }));
  };

  const onApplyFilters = () => {
    const newFilters = {
      ...appliedFilters,
      ...selectedOptions,
      ...filters,
      ...numbers,
      ...datesValues,
    };

    dispatch(singleEvidenceActions.setAppliedFilters(newFilters));
  };

  // Get Filters
  useEffect(() => {
    const getFilters = async () => {

      // Await to ensure filters are loaded before applying them
      await dispatch(
        singleEvidenceActions.getCompsFilters({
          property_id,
          is_sales: residentType === RESIDENT_TYPES.equity ? 0 : 1,
        })
      );
      dispatch(
        singleEvidenceActions.getAppliedFilters({
          property_id,
          is_sale: residentType === RESIDENT_TYPES.equity ? 0 : 1,
        })
      );

      // Reset Filters 
      setFilters({});
      setNumbers({});
      setDatesValues({});
      setSelectedOptions({});
    };

    if (getSubjectPropertyStatus === LOADING_STATUSES.succeeded) {
      getFilters();
    }
  }, [residentType, property_id, getSubjectPropertyStatus]);

  // Set Local Filters 
  useEffect(() => {
    if (appliedFilters) {
      // Selects
      const autoFillOptions = {};
      selects.forEach(selectItem => {
        const selectedValue = appliedFilters[selectItem.description];
        if (selectedValue) {
          autoFillOptions[selectItem.description] = selectedValue;
        }
      });
      setSelectedOptions(autoFillOptions);

      // Ranges
      const autoFillFilters = {};

      ranges?.forEach((range) => {
        const key = range.description;

        if (appliedFilters[key]) {
          const savedValue = appliedFilters[key];

          if (typeof savedValue === "object" && savedValue.start !== undefined && savedValue.end !== undefined) {
            autoFillFilters[key] = {
              start: savedValue.start,
              end: savedValue.end,
            };
          } else if (Array.isArray(savedValue)) {
            autoFillFilters[key] = savedValue;
          }
        }
      });
      setFilters((prevState) => ({
        ...prevState,
        ...autoFillFilters,
      }));

      // Numbers
      const autoFillNumbers = {};
      floats?.forEach((float) => {
        const key = float.description;
        autoFillNumbers[key] = appliedFilters[key];
      });
      setNumbers({
        ...numbers,
        ...autoFillNumbers,
      });

      // Dates
      dates?.forEach((dateItem) => {
        const key = dateItem.description;
        if (appliedFilters[key]) {
          setDatesValues((prevState) => ({
            ...prevState,
            [key]: appliedFilters[key],
          }));
        }
      });
    }
  }, [appliedFilters]);

  return {
    selectedOptions,
    filters,
    numbers,
    datesValues,

    handleSelectChange,
    handleFirstFilterChange,
    handleSecondFilterChange,
    setNumbers,
    handleDateChange,

    onApplyFilters,
  };
};
